import { useEffect, useState } from 'react';
import { entityId } from './entityId';

// BPM for display. Catalogue coverage (2026-11-01): 19% of tracks, 57% of
// mix plays, all from 1001tracklists, integers; key: none. So BPM is shown
// only where a single track is in focus (track page, player) and never as a
// list column that would be mostly empty.

// 60–220 = tempos DJs play; anything else is a data error (mirrors the
// server's plausibleBpm). Local tag BPM can be fractional: rounded for display.
export function validBpm(v) {
    if (v == null || v === '') return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n < 60 || n > 220) return null;
    return Math.round(n);
}

// Catalogue public ids are 16 letters/digits ("0002bN74HdW41B6s"). Local
// library rows carry numeric ids and previews "feed-3" style keys — never sent.
const isPublicId = (id) => typeof id === 'string' && /^[A-Za-z0-9]{8,32}$/.test(id) && !/^\d+$/.test(id);

// Session cache: publicId → bpm | null (null = looked up, none). Catalogue
// data, not account data — survives logout on purpose.
const cache = new Map();
const listeners = new Map(); // publicId → Set<fn>
let pending = new Set();
let timer = null;

const notify = (id) => { for (const fn of listeners.get(id) || []) fn(cache.get(id)); };

export function primeTrackBpm(tracks) {
    for (const t of Array.isArray(tracks) ? tracks : [tracks]) {
        const id = entityId(t);
        if (!isPublicId(id) || t?.bpm === undefined) continue;
        const v = validBpm(t.bpm);
        if (cache.get(id) !== v) { cache.set(id, v); notify(id); }
    }
}

async function flush() {
    timer = null;
    const ids = [...pending]; pending = new Set();
    if (!ids.length) return;
    let token = null;
    try { token = localStorage.getItem('djpal:token'); } catch (e) { /* no storage */ }
    if (!token || !window.electron?.djpal?.tracksByPublicIds) { ids.forEach((id) => cache.delete(id)); return; }
    try {
        const res = await window.electron.djpal.tracksByPublicIds(token, ids);
        const found = new Map();
        if (Array.isArray(res)) for (const t of res) { const id = entityId(t); if (id) found.set(id, validBpm(t.bpm)); }
        for (const id of ids) { cache.set(id, found.has(id) ? found.get(id) : null); notify(id); }
    } catch (e) {
        ids.forEach((id) => cache.delete(id)); // retry on a later play
    }
}

function request(id) {
    if (cache.has(id)) return;
    cache.set(id, undefined); // in flight
    pending.add(id);
    if (!timer) timer = setTimeout(flush, 60);
}

// BPM for a player item / track object: its own `bpm` when it carries one,
// otherwise a one-time catalogue lookup by its public id (trackId on player
// items). Returns null while unknown or when the catalogue has none.
export function useTrackBpm(item) {
    const own = validBpm(item?.bpm);
    const id = item ? (item.trackId || entityId(item) || null) : null;
    const lookupId = own == null && isPublicId(id) ? id : null;
    const [bpm, setBpm] = useState(() => (lookupId ? cache.get(lookupId) ?? null : null));
    useEffect(() => {
        if (!lookupId) { setBpm(null); return undefined; }
        setBpm(cache.get(lookupId) ?? null);
        const fn = (v) => setBpm(v ?? null);
        if (!listeners.has(lookupId)) listeners.set(lookupId, new Set());
        listeners.get(lookupId).add(fn);
        request(lookupId);
        return () => { listeners.get(lookupId)?.delete(fn); };
    }, [lookupId]);
    return own ?? bpm;
}

// Test hook.
export const __resetTrackBpmCache = () => { cache.clear(); listeners.clear(); pending = new Set(); if (timer) clearTimeout(timer); timer = null; };
